import React from "react";
import { X, Search, Database, Code, ListFilter } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  MapEditorPaneHeader,
  MapEditorSummaryList,
} from "./internal/MapEditorKitParts";
import type { 
  MapFeatureInspectionSheetProps
} from "../../contracts/map-workspace";

export function MapFeatureInspectionSheet({
  title,
  subtitle,
  metadata,
  summary = [],
  badges = [],
  actions = [],
  properties = [],
  rawJson,
  onAction,
  onClear,
  className,
}: MapFeatureInspectionSheetProps) {
  return (
    <div className={cn("flex flex-col h-full bg-background overflow-hidden", className)}>
      <div className="flex items-start justify-between p-4 border-b border-border bg-muted/5">
        <MapEditorPaneHeader
          title={title}
          description={subtitle}
          icon={<Search className="h-4 w-4" />}
          titleClassName="uppercase"
          actions={
            onClear ? (
              <button
                onClick={onClear}
                className="rounded-control p-1.5 text-muted-foreground transition-all hover:bg-muted hover:text-foreground"
                title="Close inspection"
              >
                <X className="h-4 w-4" />
              </button>
            ) : undefined
          }
        />
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-6">
        {/* Badges & Actions */}
        {(badges.length > 0 || actions.length > 0) && (
          <div className="flex flex-col gap-3">
            {badges.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {badges.map((badge, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 rounded-sm bg-primary/10 text-primary text-[10px] font-black uppercase tracking-wider border border-primary/20"
                  >
                    {badge}
                  </span>
                ))}
              </div>
            )}
            {actions.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {actions.map((action) => (
                  <button
                    key={action.id}
                    disabled={action.disabled}
                    onClick={() => onAction?.(action.id)}
                    className={cn(
                      "px-3 py-1.5 text-[10px] font-bold rounded-control border transition-all",
                      "bg-primary text-primary-foreground border-transparent hover:opacity-90 active:scale-[0.98]",
                      "disabled:opacity-50 disabled:bg-muted disabled:text-muted-foreground disabled:border-border disabled:active:scale-100 disabled:cursor-not-allowed"
                    )}
                    title={action.disabledReason || undefined}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* System Metadata */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Database className="w-3.5 h-3.5 text-muted-foreground/60" />
            <h4 className="text-[10px] font-black uppercase tracking-[0.15em] text-muted-foreground/80">
              System Metadata
            </h4>
          </div>
          <div className="grid grid-cols-2 gap-px bg-border border border-border rounded-lg overflow-hidden shadow-sm">
            <MetadataCell label="Source" value={metadata.source} />
            <MetadataCell label="Source Layer" value={metadata.sourceLayer} />
            <MetadataCell label="Style Layer" value={metadata.styleLayer} />
            <MetadataCell label="Geometry" value={metadata.geometryType} />
            <MetadataCell label="Internal ID" value={metadata.featureId?.toString()} span />
          </div>
        </div>

        {/* Summary Statistics */}
        {summary.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <ListFilter className="w-3.5 h-3.5 text-muted-foreground/60" />
              <h4 className="text-[10px] font-black uppercase tracking-[0.15em] text-muted-foreground/80">
                Key Metrics
              </h4>
            </div>
            <MapEditorSummaryList
              items={summary}
              className="bg-muted/5"
              valueClassName="text-primary"
            />
          </div>
        )}

        {/* Properties List */}
        {properties.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-[10px] font-black uppercase tracking-[0.15em] text-muted-foreground/80 ml-5.5">
              Property Manifest
            </h4>
            <div className="flex flex-col gap-1 px-1">
              {properties.map((prop) => (
                <div key={prop.key} className="group flex flex-col gap-0.5 p-2 rounded-control hover:bg-muted/50 border border-transparent hover:border-border/50 transition-all">
                  <span className="text-[9px] font-black uppercase tracking-wider text-muted-foreground/60 group-hover:text-primary transition-colors">
                    {prop.label}
                  </span>
                  <span className="text-[11px] font-mono font-medium text-foreground break-all">
                    {prop.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Raw JSON */}
        <div className="space-y-3 pb-4">
          <div className="flex items-center gap-2">
            <Code className="w-3.5 h-3.5 text-muted-foreground/60" />
            <h4 className="text-[10px] font-black uppercase tracking-[0.15em] text-muted-foreground/80">
              Raw Payload
            </h4>
          </div>
          <div className="overflow-hidden rounded-lg border border-border bg-muted/15">
            <div className="flex items-center justify-between border-b border-border bg-muted/30 px-3 py-1.5">
              <span className="text-[9px] font-mono text-muted-foreground/50 uppercase">
                GeoJSON Properties
              </span>
            </div>
            <div className="max-h-[300px] overflow-auto p-3 scrollbar-thin scrollbar-thumb-muted">
              <pre className="text-[10px] leading-relaxed text-foreground">
                <code>{rawJson}</code>
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetadataCell({
  label,
  value,
  span
}: { label: string; value?: string | null; span?: boolean }) {
  return (
    <div className={cn("flex flex-col gap-0.5 p-2 bg-background", span && "col-span-2")}>
      <span className="text-[9px] font-black uppercase tracking-tight text-muted-foreground/60">
        {label}
      </span>
      <span className="text-[10px] font-mono font-bold text-foreground truncate" title={value || "N/A"}>
        {value || "—"}
      </span>
    </div>
  );
}
